import React from 'react';
import PropTypes from 'prop-types';
import {get, omit} from 'lodash';

// Components
import FormButtons from '../core/form-buttons';
import Input from '../core/input';
import Permissions from '../core/permissions';
import SearchPagination from '../app/search-pagination';
import SearchTableWithSort from '../app/search-tablewithsort';
import Select from '../core/select';
import ServicesExportPanelAction from '../app/services-export-panel-action';
import ServicesSearchForm from '../app/services-search-form';
import ServicesSearchPanelAction from '../app/services-search-panel-action';
import PrioritySubmitButton from '../core/priority-submit-button';

// Containers
import withDisplayPath from '../../containers/with-display-path';
import withSearch from '../../containers/with-search';

const servicesDataKey = 'displayPathModifiers';

function DisplayPathModifiers(props) {
    const {query, searchData, totalPages} = props;

    const getColumnConfig = () => [
        {
            dataKey: 'match',
            link: '/display-path-modifiers',
            linkKey: 'networkDisplayPathModifierId',
            title: 'Match',
            type: 'link',
            width: '30%',
        },
        {
            dataKey: 'transformation',
            title: 'Transformation',
            width: '30%',
        },
        {
            dataKey: 'method',
            title: 'Method',
            width: '10%',
        },
        {
            dataKey: 'priority',
            label: 'Priority',
            title: 'Priority of a display path modifier can be \
                changed using the Drag and drop feature. \
                This feature is enabled if no filters are \
                active and priority is the first sort and is sorted in asc order',
            width: '10%',
        },
        {
            dataKey: 'network.name',
            title: 'Network',
            width: '20%',
        },
    ];

    const getPaginationProps = () => ({
        maxPages: 9,
        query,
        totalPages,
    });

    const getTablewithsortProps = () => ({
        ...props,
        activeRowKeys: ['networkDisplayPathModifierId'],
        columnConfig: getColumnConfig(),
        draggable: isDraggable(),
        hideOnError: true,
        onDragEnd,
        servicesDataKey,
    });

    const isPriorityTheFirstSortAndAscending = () => {
        const {sort} = props;
        if (sort.map((obj) => obj.column).indexOf('priority') === 0) {
            return sort.filter((obj) => obj.column === 'priority')[0].order === 'asc';
        }
        return false;
    };

    const areFiltersInactive = () => {
        const {servicesSearchData} = props;
        const rsqlObjValues = Object.values({...omit(get(servicesSearchData, 'query.rsql', {}), 'network')});
        return !!(rsqlObjValues.length === 0 || rsqlObjValues.every((currentValue) => currentValue === undefined));
    };

    const isDraggable = () => isPriorityTheFirstSortAndAscending() && areFiltersInactive();

    const isPriorityButtonDisabled = () => {
        const {error, routerParams, searchData} = props;
        const isErrorPresent = error != null;

        const routerParamNetworkDisplayPathModifierId = get(routerParams, 'networkDisplayPathModifierId', -1);
        const networkDisplayPathModifierIdArray = searchData.map((obj) => obj.networkDisplayPathModifierId);

        const isRowNotSelected = !networkDisplayPathModifierIdArray.includes(
            parseInt(routerParamNetworkDisplayPathModifierId)
        );

        return isErrorPresent || isRowNotSelected || !areFiltersInactive();
    };

    const changeToHighestPriority = (event) => {
        const {searchValues, updatePriority} = props;

        event && event.preventDefault && event.preventDefault();

        updatePriority(servicesDataKey, 'highest', searchValues.networkDisplayPathModifierId);
    };

    const changeToLowestPriority = (event) => {
        const {searchValues, updatePriority} = props;

        event && event.preventDefault && event.preventDefault();

        updatePriority(servicesDataKey, 'lowest', searchValues.networkDisplayPathModifierId);
    };

    /*
    You should implement this method if your list is draggable.
    The logic in this method is called everytime after
    a row in the list is dragged and dropped
    */
    const onDragEnd = (result) => {
        const {updatePriorityOnDrag} = props;

        // source contains the information of the the row being dragged
        // destination contains the information of where a row will be dragged to.
        const {destination, source} = result;

        // if the destination where a row is being dragged is unknown
        // then do nothing
        if (!destination) {
            return;
        }
        // do nothing when the row being dragged is dropped again on the same position
        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        updatePriorityOnDrag(servicesDataKey, destination, source);
    };

    const renderForm = () => (
        <ServicesSearchForm formName={servicesDataKey} servicesDataKey={servicesDataKey}>
            <Input
                label="Match"
                name="match"
                title="Regular expressions are also allowed. '\' characters must be entered as '\\'."
            />
            <Select label="Method" name="method">
                <option value="">All</option>
                <option value="ADD">Add</option>
                <option value="REMOVE">Remove</option>
                <option value="REPLACE">Replace</option>
            </Select>
            <Input
                label="Transformation"
                name="transformation"
                title="Regular expressions are also allowed. '\' characters must be entered as '\\'."
            />
            <FormButtons />
        </ServicesSearchForm>
    );

    return (
        <Permissions
            accessRequirement="read"
            elementService="networkDisplayPathModifier"
            notAuthorizedMessage={true}
            requiresServiceAccess={true}
        >
            <div className="display-path-modifiers">
                <ServicesExportPanelAction searchData={searchData} />
                <ServicesSearchPanelAction contentRenderer={renderForm} showPanel={true} />
                <SearchPagination {...getPaginationProps()} />
                <SearchTableWithSort {...getTablewithsortProps()} />
                <PrioritySubmitButton
                    disabled={isPriorityButtonDisabled()}
                    label="Set Highest Priority"
                    onSubmit={changeToHighestPriority}
                />
                <PrioritySubmitButton
                    disabled={isPriorityButtonDisabled()}
                    label="Set Lowest Priority"
                    onSubmit={changeToLowestPriority}
                />
            </div>
        </Permissions>
    );
}

DisplayPathModifiers.propTypes = {
    error: PropTypes.string,
    query: PropTypes.object,
    routerParams: PropTypes.object,
    searchData: PropTypes.array,
    searchValues: PropTypes.object,
    servicesSearchData: PropTypes.object,
    sort: PropTypes.array,
    totalPages: PropTypes.number,
    updatePriority: PropTypes.func,
    updatePriorityOnDrag: PropTypes.func,
};

export default withDisplayPath(withSearch(DisplayPathModifiers, servicesDataKey), servicesDataKey);
